import { useQuery } from "@tanstack/react-query";
import { type ReactNode } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  CalendarClock,
  CircleDashed,
  Clock,
  FileText,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Wallet,
} from "lucide-react";

import type {
  DealStatus,
  InsightsPartnerDealRow,
  InsightsPartnerMemberRow,
  InsightsPartnerSummary,
} from "@/api/client";
import { getInsightsPartnerSummary } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import {
  ManagerDealStatusBadge,
  MANAGER_DEAL_STATUS_LABEL,
} from "@/components/deals/manager/ManagerDealStatusBadge";
import { StatCard } from "@/components/StatCard";
import {
  CERTIFICATION_BADGE,
  CERTIFICATION_STATUS_LABEL,
  type CertificationState,
} from "@/components/training/certification";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { SortableHeader } from "@/components/ui/sortable-header";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { formatAudCents } from "@/lib/spif";
import { type SortValue } from "@/lib/tableSort";
import { useTableSort } from "@/lib/useTableSort";

export interface PartnerDeepDiveDrawerProps {
  /** The selected partner org id, or null when the drawer is closed. */
  partnerId: string | null;
  /** Fired when the drawer requests to close (overlay click / Esc / X). */
  onClose: () => void;
}

/** Format an ISO timestamp as a plain en-AU date, or "—" when absent. */
function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-AU");
}

const ROLE_LABEL: Record<"po" | "bdm", string> = { po: "PO", bdm: "BDM" };

/** Sortable columns for the deep-dive Deals ledger. */
type DealSortColumn = "deal_id" | "client" | "status" | "owner" | "value" | "expiry";

function dealSortValue(row: InsightsPartnerDealRow, column: DealSortColumn): SortValue {
  switch (column) {
    case "deal_id":
      return row.deal_id_display;
    case "client":
      return row.client_name;
    case "status":
      return MANAGER_DEAL_STATUS_LABEL[row.status as DealStatus];
    case "owner":
      return row.owner_name;
    case "value":
      return row.value_cents;
    case "expiry":
      return row.expiry ? new Date(row.expiry) : null;
  }
}

/** Sortable columns for the deep-dive Training ledger. */
type MemberSortColumn = "name" | "role" | "cert" | "extra";

function memberSortValue(row: InsightsPartnerMemberRow, column: MemberSortColumn): SortValue {
  switch (column) {
    case "name":
      return row.full_name;
    case "role":
      return ROLE_LABEL[row.role as "po" | "bdm"];
    case "cert":
      return CERTIFICATION_STATUS_LABEL[row.cert_status as CertificationState];
    case "extra":
      return row.optional_completions_count;
  }
}

/** The per-deal ledger of the partner deep-dive — Deal ID / Client / Status /
 * Owner / Value / Expiry. Sortable via the shared SortableHeader idiom; an
 * empty cohort renders "No records found." */
function DealsLedger({ rows }: { rows: InsightsPartnerDealRow[] }) {
  const { sort, toggle, sortedRows } = useTableSort(rows, dealSortValue);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableHeader column="deal_id" sort={sort} onToggle={toggle}>
            Deal ID
          </SortableHeader>
          <SortableHeader column="client" sort={sort} onToggle={toggle}>
            Client
          </SortableHeader>
          <SortableHeader column="status" sort={sort} onToggle={toggle}>
            Status
          </SortableHeader>
          {/* CHP-825: "Owner" → "Deal owner" across ledgers. */}
          <SortableHeader column="owner" sort={sort} onToggle={toggle}>
            Deal owner
          </SortableHeader>
          <SortableHeader column="value" sort={sort} onToggle={toggle} align="right">
            Value
          </SortableHeader>
          <SortableHeader column="expiry" sort={sort} onToggle={toggle}>
            Expiry
          </SortableHeader>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRows.map((r) => (
          <TableRow key={r.deal_id} data-testid={`partner-deal-row-${r.deal_id}`}>
            <TableCell className="font-medium">{r.deal_id_display ?? "—"}</TableCell>
            <TableCell>{r.client_name}</TableCell>
            <TableCell>
              <ManagerDealStatusBadge status={r.status as DealStatus} />
            </TableCell>
            <TableCell className="text-muted-foreground">
              {r.owner_name ?? "—"}
              {r.owner_role ? (
                <span className="ml-1 text-xs">({ROLE_LABEL[r.owner_role as "po" | "bdm"]})</span>
              ) : null}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatAudCents(r.value_cents)}
            </TableCell>
            <TableCell className="tabular-nums">{formatDate(r.expiry ?? null)}</TableCell>
          </TableRow>
        ))}
        {sortedRows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center text-muted-foreground">
              No records found.
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  );
}

/** The per-member training ledger of the partner deep-dive — Name / Role /
 * Cert Status / Extra Training. Sortable; empty cohort → "No records found." */
function TrainingLedger({ rows }: { rows: InsightsPartnerMemberRow[] }) {
  const { sort, toggle, sortedRows } = useTableSort(rows, memberSortValue);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableHeader column="name" sort={sort} onToggle={toggle}>
            Name
          </SortableHeader>
          <SortableHeader column="role" sort={sort} onToggle={toggle}>
            Role
          </SortableHeader>
          <SortableHeader column="cert" sort={sort} onToggle={toggle}>
            Certification
          </SortableHeader>
          <SortableHeader column="extra" sort={sort} onToggle={toggle} align="right">
            # Optional modules
          </SortableHeader>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRows.map((r) => {
          const cert = CERTIFICATION_BADGE[r.cert_status as CertificationState];
          return (
            <TableRow key={r.user_id} data-testid={`partner-member-row-${r.user_id}`}>
              <TableCell className="font-medium">{r.full_name}</TableCell>
              <TableCell>{ROLE_LABEL[r.role as "po" | "bdm"]}</TableCell>
              <TableCell>
                <Badge
                  variant={cert.variant}
                  className={cert.className}
                  data-testid={`partner-member-cert-${r.user_id}`}
                >
                  {/* CHP-840: canonical cert-status label; badge supplies colour. */}
                  {CERTIFICATION_STATUS_LABEL[r.cert_status as CertificationState]}
                </Badge>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {r.optional_completions_count === 0 ? "—" : r.optional_completions_count}
              </TableCell>
            </TableRow>
          );
        })}
        {sortedRows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={4} className="text-center text-muted-foreground">
              No records found.
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  );
}

/** A titled metric section of the drill-down modal (CHP-864): a heading +
 * sub-line over its metric-card grid. One per the four spec sections. */
function SectionHeader({
  title,
  description,
  testId,
  children,
}: {
  title: string;
  description: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section data-testid={testId}>
      <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      <p className="mb-2 text-xs text-muted-foreground">{description}</p>
      {children}
    </section>
  );
}

/** Partner deep-dive drawer (Wave 2). Clicking a row in the Channel Partner
 * Performance ledger opens this 65vw drawer onto
 * GET /insights/partners/{id}/summary: a profile/capacity header (join date, PO
 * count, BDM count), a per-org Deals row (registrations, expiring 30d, pipeline,
 * closed-won — monetary in cents → formatAudCents) over a sortable per-deal
 * ledger, and a per-org Training summary (certified / uncertified / certified %)
 * over a sortable per-member ledger. Both ledgers render "No records found."
 * when empty.
 *
 * New component — there is no PO equivalent (the PO surface has no cross-org
 * deep-dive). Reads the program-wide endpoint directly. */
export function PartnerDeepDiveDrawer({ partnerId, onClose }: PartnerDeepDiveDrawerProps) {
  const { accessToken } = useCurrentUser();
  const open = partnerId !== null;

  const { data, isLoading, error } = useQuery<InsightsPartnerSummary>({
    queryKey: ["insights", "partner-summary", partnerId, accessToken],
    queryFn: () => getInsightsPartnerSummary(accessToken!, partnerId!),
    enabled: !!accessToken && open,
  });

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:w-[65vw] sm:max-w-[65vw]"
        data-testid="partner-deep-dive-drawer"
      >
        <SheetHeader>
          <SheetTitle className="pr-8" data-testid="partner-deep-dive-title">
            {data?.company_name ?? "Partner deep-dive"}
          </SheetTitle>
          <SheetDescription>
            Deal performance and team enablement for this partner.
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <p role="status" className="mt-4 text-sm text-muted-foreground">
            Loading partner summary…
          </p>
        ) : error ? (
          <p role="alert" className="mt-4 text-sm text-destructive">
            Couldn&apos;t load this partner&apos;s summary.
          </p>
        ) : data ? (
          <div className="mt-4 space-y-6">
            {/* CHP-864 §1 — Total Sales Closed YTD (calendar year). Won/lost
                value + volume. Monetary fields are cents → formatAudCents. */}
            <SectionHeader
              title="Total Sales Closed YTD"
              description="Won and lost deals closed this calendar year."
              testId="partner-section-sales"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  title="Closed-Won Total Value"
                  value={formatAudCents(data.closed_won_ytd_value_cents)}
                  tone="success"
                  icon={TrendingUp}
                  testId="partner-closed-won-value"
                />
                <StatCard
                  title="Closed-Won Total Volume"
                  value={data.closed_won_ytd_count}
                  tone="success"
                  icon={BadgeCheck}
                  testId="partner-closed-won-volume"
                />
                <StatCard
                  title="Closed-Lost Total Value"
                  value={formatAudCents(data.closed_lost_ytd_value_cents)}
                  tone="neutral"
                  icon={TrendingDown}
                  testId="partner-closed-lost-value"
                />
                <StatCard
                  title="Closed-Lost Total Volume"
                  value={data.closed_lost_ytd_count}
                  tone="neutral"
                  icon={TrendingDown}
                  testId="partner-closed-lost-volume"
                />
              </div>
            </SectionHeader>

            {/* CHP-864 §2 — Active Pipeline (open, live deals): value + volume. */}
            <SectionHeader
              title="Active Pipeline"
              description="Open, live deals."
              testId="partner-section-pipeline"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <StatCard
                  title="Total Pipeline Value"
                  value={formatAudCents(data.pipeline_value_cents)}
                  tone="neutral"
                  icon={Wallet}
                  testId="partner-pipeline-value"
                />
                <StatCard
                  title="Pipeline Volume"
                  value={data.pipeline_volume}
                  tone="neutral"
                  icon={FileText}
                  testId="partner-pipeline-volume"
                />
              </div>
            </SectionHeader>

            {/* CHP-864 §3 — Expiring Deals (live deals closing ≤30 days): total
                risk value, deal count, and the ≤7-day urgency index. */}
            <SectionHeader
              title="Expiring Deals"
              description="Live deals closing within the next 30 days."
              testId="partner-section-expiring"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <StatCard
                  title="Total Risk Value"
                  value={formatAudCents(data.expiring_30d_value_cents)}
                  tone="neutral"
                  accent="amber"
                  icon={AlertTriangle}
                  testId="partner-risk-value"
                />
                <StatCard
                  title="Deal Count"
                  value={data.expiring_30d_count}
                  subtitle="expiring ≤30 days"
                  tone="neutral"
                  icon={CalendarClock}
                  testId="partner-expiring"
                />
                <StatCard
                  title="Urgency Index"
                  value={data.expiring_7d_count}
                  subtitle="closing ≤7 days"
                  tone="neutral"
                  accent={data.expiring_7d_count > 0 ? "red" : undefined}
                  icon={Clock}
                  testId="partner-urgency-index"
                />
              </div>
            </SectionHeader>

            {/* CHP-864 §4 — Team Knowledge: the mandatory-training partition
                (certified / not started / in progress) + proactive learning. */}
            <SectionHeader
              title="Team Knowledge"
              // Verbatim CHP-864 section copy (product text, not a
              // role-vocabulary token — same idiom as the page H1).
              // eslint-disable-next-line no-restricted-syntax
              description="Channel Partner training and development."
              testId="partner-section-knowledge"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  title="Certified"
                  value={data.team_certified_count}
                  subtitle="completed the mandatory training"
                  tone="success"
                  icon={BadgeCheck}
                  testId="partner-team-certified"
                />
                <StatCard
                  title="Not Started"
                  value={data.team_not_started_count}
                  subtitle="have not started the mandatory training"
                  tone="neutral"
                  icon={CircleDashed}
                  testId="partner-team-not-started"
                />
                <StatCard
                  title="In Progress"
                  value={data.team_in_progress_count}
                  subtitle="started but not yet completed"
                  tone="neutral"
                  icon={Clock}
                  testId="partner-team-in-progress"
                />
                <StatCard
                  title="Proactive Learning"
                  value={data.proactive_learning_count}
                  tone="success"
                  icon={Sparkles}
                  testId="partner-proactive-learning"
                />
              </div>
            </SectionHeader>

            {/* That CP's deal ledger (CHP-864: "deal ledger information at the
                bottom"). The per-member training ledger sits beneath it. */}
            <section>
              <h3 className="mb-2 text-sm font-semibold tracking-tight">Deal ledger</h3>
              <div data-testid="partner-deals-ledger">
                <DealsLedger rows={data.deal_rows} />
              </div>
            </section>
            <section>
              <h3 className="mb-2 text-sm font-semibold tracking-tight">Team training ledger</h3>
              <div data-testid="partner-training-ledger">
                <TrainingLedger rows={data.member_rows} />
              </div>
            </section>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
